/*
 * Every call to blog-api goes through this module.
 *
 * blog-api is a separate multi-tenant service (blog-api repo) that also backs
 * the public site, so what is saved here is exactly what deliva-for-web /blog
 * renders. It is not deliva-api: different host, different auth, and posts,
 * authors, topics and tags are addressed by slug rather than numeric id.
 *
 * Auth is blog-api's ADMIN_API_TOKEN, which Create React App compiles into the
 * bundle. Anyone who can load the back office can read it. Swapping to a
 * per-user scheme means changing authHeaders() here and nothing else.
 */

const BASE = (process.env.REACT_APP_BLOG_API_URI || "").replace(/\/+$/, "");
const CLIENT_ID = process.env.REACT_APP_BLOG_CLIENT_ID || "deliva";
const ADMIN_TOKEN = process.env.REACT_APP_BLOG_ADMIN_TOKEN || "";

/** Where the public site serves posts, for the URL preview in the editor. */
export const PUBLIC_BLOG_URL = (process.env.REACT_APP_BLOG_SITE_URL || "web.delivait.com/blog")
  .replace(/^https?:\/\//, "")
  .replace(/\/+$/, "");

export function blogApiConfigured() {
  return BASE !== "";
}

function authHeaders() {
  return ADMIN_TOKEN ? { Authorization: `Bearer ${ADMIN_TOKEN}` } : {};
}

function url(path, query = {}) {
  const search = new URLSearchParams({ clientId: CLIENT_ID });
  Object.entries(query).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") search.set(key, String(value));
  });
  return `${BASE}/api${path}?${search.toString()}`;
}

async function request(path, { method = "GET", query, body, auth = true } = {}) {
  if (!blogApiConfigured()) {
    throw new Error("REACT_APP_BLOG_API_URI is not set — the blog service is not configured.");
  }

  const response = await fetch(url(path, query), {
    method,
    mode: "cors",
    headers: {
      "Content-Type": "application/json",
      ...(auth ? authHeaders() : {}),
    },
    body: body === undefined ? undefined : JSON.stringify(body),
  });

  if (response.status === 204) return null;

  const payload = await response.json().catch(() => null);

  if (!response.ok) {
    // blog-api answers { error: "message", details?: [...] } — `error` is the
    // string itself, and `details` names the offending fields on a validation
    // failure, which is what an editor needs to see.
    const message = payload && typeof payload.error === "string"
      ? payload.error
      : `Request failed (${response.status})`;
    const details = payload && Array.isArray(payload.details) ? ` (${payload.details.join(", ")})` : "";
    throw Object.assign(new Error(message + details), { status: response.status });
  }

  return payload;
}

/** "My First Post!" -> "my-first-post". blog-api rejects anything else. */
export function slugify(value) {
  return String(value || "")
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 191);
}

/** Appends -2, -3, … until the slug is not already taken. */
export function uniqueSlug(base, taken) {
  const root = slugify(base) || "post";
  if (!taken.includes(root)) return root;
  let n = 2;
  while (taken.includes(`${root}-${n}`)) n++;
  return `${root}-${n}`;
}

// Posts. includeUnpublished needs the bearer token; drafts are invisible without it.
export const listPosts = ({ page = 1, pageSize = 100, search } = {}) =>
  request("/posts", { query: { page, pageSize, search, includeUnpublished: "true" } });

export const getPost = (slug) => request(`/posts/${encodeURIComponent(slug)}`);
export const createPost = (body) => request("/posts", { method: "POST", body });
export const updatePost = (slug, body) =>
  request(`/posts/${encodeURIComponent(slug)}`, { method: "PATCH", body });
export const deletePost = (slug) =>
  request(`/posts/${encodeURIComponent(slug)}`, { method: "DELETE" });

// Authors, topics, tags — a post can only reference ones that already exist.
export const listAuthors = () => request("/authors");
export const createAuthor = (body) => request("/authors", { method: "POST", body });
export const updateAuthor = (slug, body) =>
  request(`/authors/${encodeURIComponent(slug)}`, { method: "PATCH", body });
export const deleteAuthor = (slug) =>
  request(`/authors/${encodeURIComponent(slug)}`, { method: "DELETE" });

export const listTopics = () => request("/topics");
export const createTopic = (body) => request("/topics", { method: "POST", body });
export const updateTopic = (slug, body) =>
  request(`/topics/${encodeURIComponent(slug)}`, { method: "PATCH", body });
export const deleteTopic = (slug) =>
  request(`/topics/${encodeURIComponent(slug)}`, { method: "DELETE" });

export const listTags = () => request("/tags");
export const createTag = (body) => request("/tags", { method: "POST", body });
export const updateTag = (slug, body) =>
  request(`/tags/${encodeURIComponent(slug)}`, { method: "PATCH", body });
export const deleteTag = (slug) =>
  request(`/tags/${encodeURIComponent(slug)}`, { method: "DELETE" });
